import { supabase } from '../lib/supabase'
import { createServiceResponse } from '../utils/serviceUtils'
import { apiClient } from '@/utils/apiClient'
import { logger } from '@/utils/logger'

export const userService = {
  // Delete user account via Backend Custom API
  // Backend handles: anonymization, archiving, cleanup, auth deletion
  async deleteAccount() {
    try {
      const result = await apiClient.delete('/api/users/me');

      if (!result.success) {
        return createServiceResponse(null, result.error);
      }

      return createServiceResponse(result.data);
    } catch (error) {
      logger.error('UserService', 'deleteAccount error', { error: error.message });
      return createServiceResponse(null, error.message);
    }
  },

  // Get user profile
  async getUserProfile(user_id) {
    try {
      logger.debug('UserService', 'getUserProfile called', { user_id });

      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, profile_image_url, bio, location, phone, is_artist, is_admin, join_date, default_shipping_address')
        .eq('id', user_id)
        .single();

      logger.debug('UserService', 'Query result', { has_data: !!data, has_error: !!error });

      if (error) throw error;

      return createServiceResponse(data);
    } catch (error) {
      logger.error('UserService', 'getUserProfile error', { error: error.message });
      return createServiceResponse(null, error.message);
    }
  },

  // Update user profile
  async updateUserProfile(user_id, updates) {
    try {
      // Use updates directly in snake_case

      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user_id)
        .select()
        .single()

      if (error) throw error

      // Return data directly in snake_case
      return createServiceResponse(data)
    } catch (error) {
      return createServiceResponse(null, error.message)
    }
  },

  // Get artist profile
  async getArtistProfile(user_id) {
    try {
      const { data, error} = await supabase
        .from('artist_profiles')
        .select(`
          user_id,
          artist_name,
          primary_specialty,
          specialties,
          shipping_address,
          shipping_phone,
          social_links,
          verified,
          rating,
          pickup_instructions,
          user:users(
            id,
            full_name,
            profile_image_url,
            bio,
            location,
            join_date
          )
        `)
        .eq('user_id', user_id)
        .single()

      if (error) throw error

      // Return data directly in snake_case
      return createServiceResponse(data)
    } catch (error) {
      return createServiceResponse(null, error.message)
    }
  },

  // Create artist profile
  async createArtistProfile(user_id, profile_data) {
    try {
      // First update user to be an artist
      await supabase
        .from('users')
        .update({ is_artist: true })
        .eq('id', user_id)

      // Use profile data directly in snake_case

      // Create artist profile
      const { data, error } = await supabase
        .from('artist_profiles')
        .insert({
          user_id: user_id,
          ...profile_data
        })
        .select()
        .single()

      if (error) throw error
      return createServiceResponse(data)
    } catch (error) {
      return createServiceResponse(null, error.message)
    }
  },

  // Update artist profile (creates if doesn't exist)
  async updateArtistProfile(user_id, updates) {
    try {
      // Strategy: Try UPDATE first, then INSERT if profile doesn't exist
      // This avoids RLS policy conflicts with upsert

      // Step 1: Try to update existing profile
      const { data: updateData, error: updateError } = await supabase
        .from('artist_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user_id)
        .select()
        .maybeSingle() // Returns null if no rows, doesn't throw error

      // Step 2: If update succeeded, return the data
      if (updateData) {
        return createServiceResponse(updateData)
      }

      // Step 3: If no existing profile found, create new one
      if (!updateData && !updateError) {
        const { data: insertData, error: insertError } = await supabase
          .from('artist_profiles')
          .insert({
            user_id: user_id,
            ...updates,
            updated_at: new Date().toISOString()
          })
          .select()
          .single()

        if (insertError) throw insertError
        return createServiceResponse(insertData)
      }

      // Step 4: If there was an error during update, throw it
      if (updateError) throw updateError

      // Fallback (shouldn't reach here)
      throw new Error('Failed to update or create artist profile')
    } catch (error) {
      return createServiceResponse(null, error.message)
    }
  },

  // Get all artists
  async getArtists(limit = null) {
    try {
      let query = supabase
        .from('artist_profiles')
        .select(`
          user_id,
          artist_name,
          specialties,
          social_links,
          verified,
          rating,
          shipping_address,
          created_at,
          user:users(
            id,
            full_name,
            profile_image_url,
            bio,
            location,
            join_date,
            is_artist
          )
        `)
        .order('created_at', { ascending: false })

      if (limit) {
        query = query.limit(limit)
      }

      const { data, error } = await query

      if (error) throw error

      if (!data || data.length === 0) {
        return createServiceResponse(data)
      }

      // Attach artwork counts per artist
      const artistsWithCounts = await Promise.all(
        data.map(async (artist) => {
          const { count } = await supabase
            .from('artworks')
            .select('id', { count: 'exact', head: true })
            .eq('artist_id', artist.user_id)
            .eq('status', 'published')

          return {
            ...artist,
            artworks: { count: count || 0 }
          }
        })
      )

      // Filtros duros para /artistas:
      // - Usuario marcado como artista en BD (is_artist=true)
      // - Al menos 1 obra publicada (criterio real de actividad en el marketplace)
      // La foto de perfil NO es requisito duro — se usa en el soft ranking abajo
      // para darle menos visibilidad a perfiles incompletos.
      const qualified = artistsWithCounts.filter((artist) => {
        const is_artist = artist.user?.is_artist === true
        const has_artworks = (artist.artworks?.count || 0) >= 1
        return is_artist && has_artworks
      })

      // Soft ranking: score por completitud (foto, verified, cantidad de obras).
      // Artistas con perfil mas completo aparecen primero sin excluir a los incompletos.
      const scored = qualified.map((artist) => {
        const has_photo = Boolean(artist.user?.profile_image_url)
        const works_count = artist.artworks?.count || 0
        let score = 0
        if (has_photo) score += 3
        if (artist.verified) score += 2
        score += Math.min(works_count, 10) * 0.2
        return { artist, score }
      })
      scored.sort((a, b) => b.score - a.score)

      return createServiceResponse(scored.map(({ artist }) => artist))
    } catch (error) {
      return createServiceResponse(null, error.message)
    }
  }
}